import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

// accounts: array of { user_id, email, name, picture, username, token, auth_provider }
function readAccounts() {
  try { return JSON.parse(localStorage.getItem("accounts") || "[]"); } catch { return []; }
}
function writeAccounts(arr) {
  localStorage.setItem("accounts", JSON.stringify(arr));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState(readAccounts());
  const [loading, setLoading] = useState(true);

  const setActive = useCallback((account) => {
    if (account) {
      localStorage.setItem("token", account.token);
      localStorage.setItem("activeUserId", account.user_id);
      setUser({ ...account });
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("activeUserId");
      setUser(null);
    }
  }, []);

  const addAccount = useCallback((account) => {
    const next = [...readAccounts().filter((a) => a.user_id !== account.user_id), account];
    writeAccounts(next);
    setAccounts(next);
    setActive(account);
  }, [setActive]);

  const switchAccount = useCallback((user_id) => {
    const acc = readAccounts().find((a) => a.user_id === user_id);
    if (acc) {
      setActive(acc);
      // Force a full reload so every context + axios state starts fresh for the new user.
      setTimeout(() => { window.location.href = "/projects"; }, 50);
    }
    return acc;
  }, [setActive]);

  const removeAccount = useCallback((user_id) => {
    const next = readAccounts().filter((a) => a.user_id !== user_id);
    writeAccounts(next);
    setAccounts(next);
    if (user?.user_id === user_id) {
      if (next.length > 0) setActive(next[0]);
      else setActive(null);
    }
  }, [setActive, user]);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) { setUser(null); return; }
      const { data } = await api.get("/auth/me");
      setUser(data);
      // sync latest into accounts store
      const accs = readAccounts();
      const idx = accs.findIndex((a) => a.user_id === data.user_id);
      const record = { ...data, token: token || accs[idx]?.token };
      if (idx >= 0) accs[idx] = record; else if (token) accs.push(record);
      writeAccounts(accs);
      setAccounts(accs);
    } catch (err) {
      // Clear a stale/expired token so the login form shows cleanly.
      if (err?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("activeUserId");
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithCreds = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    addAccount({ ...data.user, token: data.token });
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    addAccount({ ...data.user, token: data.token });
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    // Clear wallpaper immediately so it doesn't leak to the next user
    document.body.style.backgroundImage = "";
    document.body.style.backgroundColor = "";
    document.body.classList.remove("cresc-has-bg","cresc-bg-calendar","cresc-bg-documents");
    localStorage.removeItem("token");
    localStorage.removeItem("activeUserId");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, loading,
      accounts, switchAccount, addAccount, removeAccount,
      loginWithCreds, register, logout, checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
